/**
 * Visibility blur policy for the web client.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/AppSwitcherBlurPolicy.swift` — the iOS
 * policy fires `showOverlay` just before the OS takes an app-switcher snapshot
 * (at `willResignActive`). The web equivalent fires when the tab becomes
 * hidden (`document.visibilityState === 'hidden'`).
 *
 * PLATFORM GAP: iOS can blur *before* the OS snapshot fires (at
 * `willResignActive`). The web `visibilitychange` event fires *after* the
 * browser has already hidden the tab — there is no pre-snapshot hook.
 * The DOM clear still provides meaningful protection against:
 *   - Screen sharing: content is cleared while the tab is not in focus
 *   - Passive shoulder-surfing: message bodies disappear on alt-tab
 *
 * Pure functions only. NO DOM event listeners. NO Solid reactivity.
 * Wiring is done by the caller (e.g. a SolidJS effect or a plain addEventListener).
 */

import type { ScreenSecuritySettings } from "./ScreenSecuritySettings";

/** Possible decisions returned by `decideBlur`. */
export type BlurDecision = "showBlurOverlay" | "noBlur";

/**
 * Decide whether the app should apply a blur/clear overlay given the current
 * document visibility state and the user's screen-security settings.
 *
 * @param visibilityState - `document.visibilityState` value at the time of the event.
 * @param settings        - Current screen-security settings.
 * @returns `'showBlurOverlay'` when sensitive content should be hidden;
 *          `'noBlur'` otherwise.
 *
 * iOS analogue: `AppSwitcherBlurPolicy.decide(state:, settings:)`.
 *
 * Decision table:
 * | visibilityState | settings.blurOnHidden | result            |
 * |-----------------|----------------------|-------------------|
 * | 'hidden'        | true                 | showBlurOverlay   |
 * | 'hidden'        | false                | noBlur            |
 * | 'visible'       | (any)                | noBlur            |
 * | 'prerender'     | (any)                | noBlur            |
 */
export function decideBlur(
  visibilityState: DocumentVisibilityState | string,
  settings: Pick<ScreenSecuritySettings, "blurOnHidden">,
): BlurDecision {
  if (visibilityState === "hidden" && settings.blurOnHidden) {
    return "showBlurOverlay";
  }
  return "noBlur";
}
